import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Layers, Save, StickyNote } from "lucide-react";
import Spinner from "../components/Spinner";
import Button from "../components/Button";
import StudyLayout from "../components/StudyLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import NavigationButtons from "../components/NavigationButtons";
import ScrollToTopButton from "../components/ScrollToTopButton";
import { communitiesAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import { formatReadingEstimate } from "../utils/readingTime";
import "./CommunityLesson.css";

const CommunityLesson = () => {
  const { communityId, lessonId } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const breadcrumbs = useMemo(
    () => [
      { label: "MySpace", path: "/myspace" },
      { label: "Communities", path: "/communities" },
      {
        label: lesson?.communityName || "Community",
        path: `/communities/${communityId}`,
      },
      { label: lesson?.chapter || "Lesson", path: null },
    ],
    [communityId, lesson],
  );

  useEffect(() => {
    const loadLesson = async () => {
      try {
        const response = await communitiesAPI.getLesson(communityId, lessonId);
        setLesson(response.data.lesson);
      } catch (error) {
        addToast(
          error.response?.data?.details?.[0] || "Failed to load lesson",
          "error-warning",
        );
        navigate(`/communities/${communityId}`);
      } finally {
        setLoading(false);
      }
    };

    loadLesson();
  }, [addToast, communityId, lessonId, navigate]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await communitiesAPI.saveLesson(communityId, lessonId);
      addToast("Lesson saved to Saved folder", "success-info", 3500, true);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to save lesson",
        "error-warning",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner visible={true} />;
  }

  if (!lesson) {
    return null;
  }

  const readingEstimateLabel = formatReadingEstimate(lesson);

  const lessonActions = (
    <div className="community-lesson-top-actions">
      <Link
        className={`community-lesson-action-link community-lesson-control-button community-lesson-action-link-round community-lesson-notes-button ${
          lesson.hasNotes ? "" : "is-disabled"
        }`.trim()}
        to={lesson.hasNotes ? `/community/${communityId}/notes/${lessonId}` : "#"}
        title={lesson.hasNotes ? "Notes" : "No notes available"}
        aria-label={lesson.hasNotes ? "Notes" : "No notes available"}
        onClick={(event) => {
          if (!lesson.hasNotes) {
            event.preventDefault();
            addToast("No notes available", "error-warning");
          }
        }}
      >
        <StickyNote size={16} />
      </Link>
      <Link
        className={`community-lesson-action-link community-lesson-control-button community-lesson-action-link-round community-lesson-flashcards-button ${
          lesson.hasFlashcards ? "" : "is-disabled"
        }`.trim()}
        to={
          lesson.hasFlashcards
            ? `/community/${communityId}/flashcards/${lessonId}`
            : "#"
        }
        title={lesson.hasFlashcards ? "Flashcards" : "No flashcards available"}
        aria-label={lesson.hasFlashcards ? "Flashcards" : "No flashcards available"}
        onClick={(event) => {
          if (!lesson.hasFlashcards) {
            event.preventDefault();
            addToast("No flashcards available", "error-warning");
          }
        }}
      >
        <Layers size={16} />
      </Link>
      {lesson.canSave && (
        <Button
          className="community-lesson-control-button community-lesson-save-button"
          onClick={handleSave}
          disabled={saving}
          title={saving ? "Saving..." : "Save"}
          aria-label={saving ? "Saving..." : "Save"}
        >
          <Save size={16} />
        </Button>
      )}
    </div>
  );

  return (
    <StudyLayout
      className="community-lesson-layout"
      breadcrumbs={<Breadcrumbs path={breadcrumbs} />}
      buttons={lessonActions}
    >
    <div className="community-lesson-page">
      <NavigationButtons hideExitButton hideActiveButton />
      <div className="community-lesson-shell">
        <div className="community-lesson-header">
          <div className="community-lesson-community">
            {lesson.communityName}
          </div>
          {lesson.chapter && <h1>{lesson.chapter}</h1>}
          {(lesson.author || readingEstimateLabel) && (
            <div className="community-lesson-meta-row">
              {lesson.author && <p>{lesson.author}</p>}
              {readingEstimateLabel && <span>{readingEstimateLabel}</span>}
            </div>
          )}
        </div>

        <article className="community-lesson-body">{lesson.body}</article>
      </div>
      <ScrollToTopButton scrollContainerSelector=".app-app" />
    </div>
    </StudyLayout>
  );
};

export default CommunityLesson;
